import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Checkbox, Form, Input, Modal, Select, Spin } from 'antd';
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CloseOutlined,
  DeleteOutlined,
  FileOutlined,
  UserOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import styled from '@emotion/styled';

import {
  Container,
  PrimaryButtonV2,
  SecondaryButton,
  formLayout,
  formValidateMessages,
  AddFormItemLabel,
  device,
  isPromotionEligible,
} from 'utils';

import { ProjectsActions, AlertActions } from 'redux/actions';

import StarIcon from 'scenes/Project/v2/components/DetailComponents/Icons/personal/Star';
import HandIcon from 'scenes/Project/v2/components/DetailComponents/Icons/personal/Strong';
import HeartIcon from 'scenes/Project/v2/components/DetailComponents/Icons/personal/Heart';
import Star2Icon from 'scenes/Project/v2/components/DetailComponents/Icons/personal/Star2';
import FingerIcon from 'scenes/Project/v2/components/DetailComponents/Icons/personal/Finger';
import HaciIcon from 'scenes/Project/v2/components/DetailComponents/Icons/personal/Haci';
import HijabIcon from 'scenes/Project/v2/components/DetailComponents/Icons/personal/Hijab';
import MosqueIcon from 'scenes/Project/v2/components/DetailComponents/Icons/personal/Mosque';
import QuranIcon from 'scenes/Project/v2/components/DetailComponents/Icons/personal/Quran';
import LightsIcon from 'scenes/Project/v2/components/DetailComponents/Icons/personal/Lights';
import SupporterIcon from 'scenes/Project/v2/components/DetailComponents/Icons/SupporterIcon';
import QuestionMark from 'scenes/Project/v2/components/DetailComponents/Icons/QuestionMark';
import ReceiptCheck from 'scenes/Project/v2/components/DetailComponents/Icons/ReceiptCheck';

import CheckMarkIcon from './Icons/CheckMark';
import BlueCheck from './Icons/BlueCheck';

const FormItem = Form.Item;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 32px;
  width: 600px;
  max-width: 600px;

  @media (max-width: 640px) {
    width: 100%;
    max-width: 100%;
  }
`;

const StyledForm = styled(Form)`
  width: 100%;

  .ant-form-item {
    margin-bottom: 0;
    width: 100%;
  }

  .ant-form-item-label {
    padding-bottom: 6px;
  }

  .ant-form-item-label > label {
    height: 20px;
    font-family: Inter, sans-serif;
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    color: #414651;
  }

  .ant-form-item-label > label::after {
    display: none;
  }

  .ant-form-item-required::before {
    display: none !important;
  }

  .ant-form-item-required::after {
    display: inline-block !important;
    content: '*';
    margin-left: 2px;
    color: #9dbc60;
    font-size: 14px;
    line-height: 20px;
  }
`;

const FormContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 100%;
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

const ProfileHeaderSection = styled(Section)`
  gap: 32px;
`;

const SectionTitle = styled.div`
  margin: 0;
  font-family: Inter, sans-serif;
  font-weight: 600;
  font-size: 30px;
  line-height: 38px;
  color: #181d27;
`;

const SectionText = styled.div`
  margin: 0;
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 18px;
  line-height: 28px;
  color: #535862;
`;

const ProfileTypeSwitch = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ProfileTypeSwitchText = styled.span`
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #535862;
`;

const ProfileTypeSwitchButton = styled.button`
  border: 0;
  padding: 0;
  background: transparent;
  font-family: Inter, sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  color: #3f621a;
  cursor: pointer;
`;

const LabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const LabelText = styled.span`
  font-family: Inter, sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #414651;
`;

const RequiredMark = styled.span`
  color: #9dbc60;
`;

const HelpIconWrap = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
`;

const VisibilityModalContent = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 600px;
  max-width: calc(100vw - 32px);
  background: #ffffff;
  box-shadow: 0px 20px 24px -4px rgba(10, 13, 18, 0.08),
    0px 8px 8px -4px rgba(10, 13, 18, 0.03),
    0px 3px 3px -1.5px rgba(10, 13, 18, 0.04);
  border-radius: 12px;
  overflow: hidden;
`;

const VisibilityModalBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  padding: 24px 24px 0;
`;

const VisibilityModalIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 9999px;
  background: #e6f4d7;
  color: #9dbc60;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
`;

const VisibilityModalSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  width: 100%;
`;

const VisibilityModalTitle = styled.h2`
  margin: 0;
  font-family: Inter, sans-serif;
  font-weight: 600;
  font-size: 18px;
  line-height: 28px;
  color: #181d27;
`;

const VisibilityModalSubtitle = styled.h3`
  margin: 0;
  font-family: Inter, sans-serif;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  color: #181d27;
`;

const VisibilityModalText = styled.div`
  margin: 0;
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #535862;
`;

const VisibilityModalClose = styled.button`
  position: absolute;
  right: 16px;
  top: 16px;
  width: 44px;
  height: 44px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #a4a7ae;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 24px;

  &:hover {
    background: #f5f5f5;
  }
`;

const VisibilityModalActions = styled.div`
  padding: 32px 24px 24px;
  width: 100%;
`;

const VisibilityModalButton = styled.button`
  width: 100%;
  height: 44px;
  border: 1px solid #9dbc60;
  border-radius: 8px;
  background: #9dbc60;
  box-shadow: 0px 1px 2px rgba(10, 13, 18, 0.05),
    inset 0px 0px 0px 1px rgba(10, 13, 18, 0.18),
    inset 0px -2px 0px rgba(10, 13, 18, 0.05);
  color: #ffffff;
  font-family: Inter, sans-serif;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  cursor: pointer;
`;

const SelectWrap = styled.div`
  width: 100%;

  .ant-select {
    width: 100%;
  }

  .ant-select-selector {
    height: 40px !important;
    padding: 8px 12px !important;
    border-radius: 8px !important;
    border: 1px solid #d5d7da !important;
    box-shadow: 0px 1px 2px rgba(10, 13, 18, 0.05) !important;
    display: flex;
    align-items: center;
  }

  .ant-select-selection-item {
    font-family: Inter, sans-serif;
    font-weight: 500;
    font-size: 16px;
    line-height: 24px !important;
    color: #181d27;
  }
`;

const HintText = styled.div`
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 14px;
  line-height: 20px;
  color: #535862;
`;

const AvatarRow = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;
  @media ${device.allMobile} {
    display: grid;

    grid-template-columns: repeat(5, 1fr);

    gap: 12px;

    justify-items: center;
  }
`;

const AvatarButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 9999px;
  border: 2px solid ${({ $active }) => ($active ? '#669f2a' : 'transparent')};
  background: ${({ $bg }) => $bg};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  position: relative;
`;

const AvatarInner = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  color: ${({ $color }) => $color};
`;

const LogoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
`;

const LogoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
`;

const LogoAvatarWrap = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 9999px;
  background: #f5f5f5;
  border: 0.75px solid rgba(0, 0, 0, 0.08);
  position: relative;
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;

`;

const LogoImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
    border-radius: 9999px;
`;

const LogoBadge = styled.div`
  position: absolute;
  right: 0;
    z-index: 999;
  bottom: 0;
  width: 20px;
  height: 20px;
`;

const LogoTexts = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const LogoTitle = styled.div`
  font-family: Inter, sans-serif;
  font-weight: 600;
  font-size: 18px;
  line-height: 28px;
  color: #181d27;
`;

const LogoSubtitle = styled.div`
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: #535862;
`;

const LogoActions = styled.div`
  display: flex;
  gap: 8px;
  width: 100%;
`;

const LogoActionButton = styled.button`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  padding: 10px 14px;
  gap: 4px;
  height: 40px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  color: ${({ $tone }) => ($tone === 'green' ? '#3f621a' : '#535862')};

  &:hover {
    background: ${({ $tone }) => ($tone === 'green' ? '#f6fbe8' : '#f8fafc')};
  }
`;

const LogoActionText = styled.span`
  font-family: Inter, sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
`;

const HiddenFileInput = styled.input`
  display: none !important;
`;

const PreviewCard = styled.div`
  width: 100%;
`;

const PreviewHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const PreviewAvatar = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 9999px;
  background: ${({ $bg }) => $bg || '#f5f5f5'};
  border: 0.75px solid rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  flex: none;
`;

const PreviewBadge = styled.div`
  position: absolute;
  right: 0;
  bottom: 0;
  width: 20px;
  height: 20px;
`;

const PreviewTexts = styled.div`
  display: flex;
  flex-direction: column;
`;

const PreviewTitle = styled.div`
  font-family: Inter, sans-serif;
  font-weight: 600;
  font-size: 18px;
  line-height: 28px;
  color: #181d27;
`;

const PreviewMeta = styled.div`
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: #535862;
`;

const InfoBox = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;
  padding: 16px;
  background: #ffffff;
  border: 1px solid #d5d7da;
  box-shadow: 0px 1px 2px rgba(10, 13, 18, 0.05);
  border-radius: 12px;
  box-sizing: border-box;
`;

const InfoIconWrap = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 9999px;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
`;

const InfoContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

const InfoTitle = styled.div`
  font-family: Inter, sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  color: #414651;
  
`;

const InfoText = styled.div`
  /* Text sm/Regular */
  font-family: 'Inter';
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  /* or 143% */

  color: #535862;

`;

const PrivacyLink = styled.a`
  color: #3f621a !important;
  text-decoration: underline !important;
`;

const UploadCard = styled.div`
  width: 100%;
`;

const UploadArea = styled.button`
  width: 100%;
  min-height: 126px;
  border: 1px solid ${({ $hasError }) => ($hasError ? '#d92d20' : '#e9eaeb')};
  border-radius: 12px;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  cursor: pointer;
  padding: 16px 24px;
  gap: 12px;
`;

const UploadError = styled.div`
  margin-top: 8px;
  color: #d92d20;
  font-family: Inter, sans-serif;
  font-size: 14px;
  line-height: 20px;
`;

const UploadIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  border: 1px solid #e9eaeb;
  box-shadow: 0px 1px 2px rgba(10, 13, 18, 0.05),
    inset 0px 0px 0px 1px rgba(10, 13, 18, 0.18),
    inset 0px -2px 0px rgba(10, 13, 18, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #414651;
  background: #ffffff;
`;

const UploadTopText = styled.div`
  font-family: Inter, sans-serif;
  font-size: 14px;
  line-height: 20px;
  color: #535862;
  text-align: center;

  strong {
    color: #3f621a;
    font-weight: 600;
  }
`;

const UploadHint = styled.div`
  font-family: Inter, sans-serif;
  font-size: 12px;
  line-height: 18px;
  color: #535862;
  text-align: center;
`;

const Queue = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const QueueItem = styled.div`
  width: 100%;
  height: 72px;
  background: #ffffff;
  border: 1px solid #e9eaeb;
  border-radius: 12px;
  padding: 16px;
  box-sizing: border-box;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
`;

const QueueContent = styled.div`
  display: flex;
  gap: 8px;
  min-width: 0;
  color: #a4a7ae;
`;

const QueueTexts = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const QueueTitle = styled.div`
  font-family: Inter, sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #414651;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const QueueMeta = styled.div`
  font-family: Inter, sans-serif;
  font-size: 14px;
  line-height: 20px;
  color: #535862;
`;

const RemoveButton = styled.button`
  border: none;
  background: transparent;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #a4a7ae;
  flex: none;
`;

const AddTextArea = styled(Input)`
  height: 40px !important;
  resize: none !important;
  font-size: 16px !important;
  line-height: 24px !important;
  border-radius: 8px !important;
  border-color: #d5d7da !important;
  box-shadow: 0px 1px 2px rgba(10, 13, 18, 0.05) !important;
  input {
    height: 32px !important;
  }
`;

const ReceiptDescription = styled.div`
  margin-top: -6px;
  margin-left: 28px;
  font-family: Inter, sans-serif;
  font-size: 14px;
  line-height: 20px;
  color: #535862;
`;

const Actions = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const legacyAvatarKeys = {
  'hand-1': 'hand',
  'star-1': 'star',
  'heart-1': 'heart',
};

const normalizeAvatarKey = (avatarKey) =>
  legacyAvatarKeys[avatarKey] || avatarKey;

export const avatarOptions = [
  {
    key: 'star',
    baseKey: 'star',
    icon: <StarIcon />,
    bg: '#EFF6FF',
    color: '#51A2FF',
  },
  {
    key: 'heart',
    baseKey: 'heart',
    icon: <HeartIcon />,
    bg: '#FDF2F8',
    color: '#FB64B6',
  },
  {
    key: 'star2',
    baseKey: 'star2',
    icon: <Star2Icon />,
    bg: '#F4F3FF',
    color: '#BDB4FE',
  },
  {
    key: 'finger',
    baseKey: 'finger',
    icon: <FingerIcon />,
    bg: '#F8F9FC',
    color: '#717BBC',
  },
  {
    key: 'haci',
    baseKey: 'haci',
    icon: <HaciIcon />,
    bg: '#EFF8FF',
    color: '#84CAFF',
  },
  {
    key: 'hijab',
    baseKey: 'hijab',
    icon: <HijabIcon />,
    bg: '#FDF2FA',
    color: '#FAA7E0',
  },
  {
    key: 'mosque',
    baseKey: 'mosque',
    icon: <MosqueIcon />,
    bg: '#FFF4ED',
    color: '#FF9C66',
  },
  {
    key: 'quran',
    baseKey: 'quran',
    icon: <QuranIcon />,
    bg: '#F7F5F3',
    color: '#79573A',
  },
  {
    key: 'lights',
    baseKey: 'lights',
    icon: <LightsIcon />,
    bg: '#FEFBE8',
    color: '#FAC515',
  },
  {
    key: 'hand',
    baseKey: 'hand',
    icon: <HandIcon />,
    bg: '#FFF7ED',
    color: '#FF8904',
  },
];

const organizationTypeOptions = [
  { value: 'mosque', label: 'Moschee' },
  { value: 'community', label: 'Gemeinde' },
  { value: 'foundation', label: 'Stiftung' },
  { value: 'religious_organization', label: 'Religiöse Organisation' },
  { value: 'aid_organization', label: 'Hilfsorganisation' },
  { value: 'educational_organization', label: 'Bildungsorganisation' },
  { value: 'youth_organization', label: 'Jugendorganisation' },
  { value: 'cultural_organization', label: 'Kulturorganisation' },
  { value: 'sports_organization', label: 'Sportorganisation' },
  { value: 'social_organization', label: 'Soziale Organisation' },
  { value: 'student_organization', label: 'Studentenorganisation' },
  { value: 'initiative', label: 'Initiative' },
  { value: 'network', label: 'Netzwerk' },
  { value: 'other_organization', label: 'Sonstige Organisation' },
];

class PersonalInformation extends Component {
  static propTypes = {
    updateDraft: PropTypes.func.isRequired,
    uploadFiles: PropTypes.func.isRequired,
    project: PropTypes.object.isRequired,
    loading: PropTypes.bool.isRequired,
    errors: PropTypes.object.isRequired,
    projectId: PropTypes.string,
    currentStep: PropTypes.number.isRequired,
    setCanMove: PropTypes.func.isRequired,
    showModal: PropTypes.bool.isRequired,
    selectedStep: PropTypes.number.isRequired,
    setSubmitFromModal: PropTypes.func.isRequired,
    handleExitEditForm: PropTypes.func,
    editProject: PropTypes.bool,
    promotionModuleEnabled: PropTypes.bool,
    minFundingGoal: PropTypes.number,
    handleSteps: PropTypes.func.isRequired,
    user: PropTypes.object,
  };

  constructor(props) {
    super(props);

    const existingLogo = props.project?.organization_logo || null;
    const existingDocs =
      props.project?.support_documents || props.project?.supportDocuments || [];

    const loadedDocs = existingDocs.map((doc) => ({
      id: doc.id,
      filename: doc.filename,
      byte_size: doc.byte_size,
      url: doc.url,
      isExisting: true,
    }));

    this.fileInputRef = React.createRef();
    this.documentsSectionRef = React.createRef();
    this.organizationLogoInputRef = React.createRef();
    this.formRef = React.createRef();

    this.state = {
      visibility: this.getProjectVisibility(props.project),
      selectedAvatar: this.getProjectSelectedAvatar(props.project),
      organization_name: props.project?.organization_name || '',
      organization_type: props.project?.organization_type || undefined,
      organizationLogoPreview: existingLogo,
      organizationLogoFile: null,
      removeOrganizationLogoRequested: false,
      files: [],
      existingDocs: loadedDocs,
      documentError: '',
      donation_receipts_possible:
        props.project?.fundingTarget?.donation_receipts_possible || false,
      belege_email:
        props.project?.belege_email || props.project?.initiator_email || '',
      showVisibilityInfoModal: false,
    };
  }



  componentDidUpdate(prevProps) {
    if (prevProps.project !== this.props.project) {
      const existingDocs =
        this.props.project?.support_documents ||
        this.props.project?.supportDocuments ||
        [];

      const loadedDocs = existingDocs.map((doc) => ({
        id: doc.id,
        filename: doc.filename,
        byte_size: doc.byte_size,
        url: doc.url,
        isExisting: true,
      }));

      const visibility = this.getProjectVisibility();
      const selectedAvatar = this.getProjectSelectedAvatar();

      this.formRef.current?.setFieldsValue({
        visibility,
        selected_avatar: selectedAvatar,
        organization_name:
          this.props.project?.organization_name || this.state.organization_name,
        organization_type:
          this.props.project?.organization_type || this.state.organization_type,
        belege_email:
          this.props.project?.belege_email ||
          this.state.belege_email ||
          this.props.project?.initiator_email,
        donation_receipts_possible:
          this.props.project?.fundingTarget?.donation_receipts_possible ||
          this.state.donation_receipts_possible,
      });

      if (!this.state.files.length && !this.state.organizationLogoFile) {
        if (this.state.organizationLogoPreview?.startsWith?.('blob:')) {
          URL.revokeObjectURL(this.state.organizationLogoPreview);
        }

        this.setState({
          visibility,
          selectedAvatar,
          organization_name: this.props.project?.organization_name || '',
          organization_type: this.props.project?.organization_type || '',
          organizationLogoPreview:
            this.props.project?.organization_logo || null,
          organizationLogoFile: null,
          removeOrganizationLogoRequested: false,
          existingDocs: loadedDocs,
          donation_receipts_possible:
            this.props.project?.fundingTarget?.donation_receipts_possible ||
            false,
          belege_email: this.props.project?.belege_email || this.props.project?.initiator_email || '',
        });
      }
    }
  }

  componentWillUnmount() {
    if (this.state.organizationLogoPreview?.startsWith?.('blob:')) {
      URL.revokeObjectURL(this.state.organizationLogoPreview);
    }
  }

  isEventProject = (project = this.props.project) =>
    project?.fundingTarget?.is_ticketable ?? false;

  isPersonalizationLocked = () => {
    const personalizationDone =
      this.props.project?.is_personalization_done ??
      this.props.project?.isPersonalizationDone;

    return Boolean(
      this.props.editProject &&
        (personalizationDone === true ||
          personalizationDone === 'true' ||
          personalizationDone === 1)
    );
  };

  normalizeVisibility = (visibility, project = this.props.project) => {
    if (this.isEventProject(project) && visibility === 'anonymous') {
      return 'public';
    }

    return visibility || 'public';
  };

  getProjectVisibility = (project = this.props.project) =>
    this.normalizeVisibility(
      project?.visibility ||
        project?.personalization?.visibility ||
        project?.personalInformation?.visibility ||
        project?.personal_information?.visibility ||
        'public',
      project
    );

  getVisibilityOptions = () => {
    const options = [
      {
        value: 'public',
        label: `Vollständiger Name (${getFullName(
          this.props.user?.first_name,
          this.props.user?.last_name
        )})`,
      },
      {
        value: 'verified',
        label: `Nachname gekürzt (${shortenName(
          this.props.user?.first_name,
          this.props.user?.last_name
        )})`,
      },
    ];

    if (!this.isEventProject()) {
      options.push({
        value: 'anonymous',
        label: 'Vollständig anonym (muss verifiziert sein)',
      });
    }

    return options;
  };

  getProjectSelectedAvatar = (project = this.props.project) =>
    normalizeAvatarKey(
      project?.selected_avatar ||
        project?.personalization?.selected_avatar ||
        project?.personalInformation?.selected_avatar ||
        project?.personal_information?.selected_avatar ||
        (this.props.editProject ? null : 'star')
    );

  getSelectedAvatarBaseKey = () => {
    const current = avatarOptions.find(
      (item) =>
        item.key === this.state.selectedAvatar ||
        item.baseKey === this.state.selectedAvatar
    );

    return current?.baseKey || null;
  };

  getSelectedAvatarOption = () =>
    avatarOptions.find(
      (item) =>
        item.key === this.state.selectedAvatar ||
        item.baseKey === this.state.selectedAvatar
    );

  getOrganizationTypeLabel = () => {
    const current = organizationTypeOptions.find(
      (item) => item.value === this.state.organization_type
    );

    return current?.label || 'Organisationstyp';
  };

  normalizeFiles = (candidateFiles) => {
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
    const filteredFiles = [];

    candidateFiles.forEach((file) => {
      if (file.size > 3 * 1024 * 1024) {
        AlertActions.error(`❌ ${file.name} ist größer als 3MB.`);
        return;
      }

      if (!allowedTypes.includes(file.type)) {
        AlertActions.error(
          `❌ ${file.name} hat einen nicht unterstützten Dateityp.`
        );
        return;
      }

      filteredFiles.push(file);
    });

    return filteredFiles;
  };

  handleFilePick = (event) => {
    const selectedFiles = this.normalizeFiles(
      Array.from(event.target.files || [])
    );

    if (!selectedFiles.length) {
      event.target.value = '';
      return;
    }

    this.setState((prevState) => {
      const combined = [...prevState.files, ...selectedFiles];

      if (combined.length > 5) {
        AlertActions.error('Du kannst nur bis zu 5 Dateien hochladen.');
        return null;
      }

      return { files: combined, documentError: '' };
    });

    event.target.value = '';
  };

  handleDrop = (event) => {
    event.preventDefault();

    const droppedFiles = this.normalizeFiles(
      Array.from(event.dataTransfer.files || [])
    );

    if (!droppedFiles.length) return;

    this.setState((prevState) => {
      const combined = [...prevState.files, ...droppedFiles];

      if (combined.length > 5) {
        AlertActions.error('Du kannst nur bis zu 5 Dateien hochladen.');
        return null;
      }

      return { files: combined, documentError: '' };
    });
  };

  handleUploadAreaClick = () => {
    this.fileInputRef.current?.click();
  };

  handleOrganizationLogoClick = () => {
    this.organizationLogoInputRef.current?.click();
  };

  handleOrganizationLogoPick = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];

    if (!allowedTypes.includes(file.type)) {
      AlertActions.error('❌ Das Logo muss ein PNG, JPEG oder WEBP sein.');
      return;
    }

    if (file.size > 3 * 1024 * 1024) {
      AlertActions.error(`❌ ${file.name} ist größer als 3MB.`);
      return;
    }

    if (this.state.organizationLogoPreview?.startsWith?.('blob:')) {
      URL.revokeObjectURL(this.state.organizationLogoPreview);
    }

    this.setState({
      organizationLogoPreview: URL.createObjectURL(file),
      organizationLogoFile: file,
      removeOrganizationLogoRequested: false,
    });
  };

  removeOrganizationLogo = () => {
    if (this.state.organizationLogoPreview?.startsWith?.('blob:')) {
      URL.revokeObjectURL(this.state.organizationLogoPreview);
    }

    this.setState({
      organizationLogoPreview: null,
      organizationLogoFile: null,
      removeOrganizationLogoRequested: true,
    });
  };

  removeFile = (indexToRemove) => {
    this.setState((prevState) => ({
      files: prevState.files.filter((_, index) => index !== indexToRemove),
    }));
  };

  removeExistingDoc = (docId) => {
    this.setState((prevState) => ({
      existingDocs: prevState.existingDocs.filter((doc) => doc.id !== docId),
    }));
  };

  handleReturn = () => {
    if (this.props.editProject && this.props.handleExitEditForm) {
      this.props.handleExitEditForm();
      return;
    }

    if (
      this.props.promotionModuleEnabled &&
      isPromotionEligible(this.props.project, this.props.minFundingGoal)
    ) {
      if (this.isEventProject()) {
        this.props.handleSteps(7);
      } else {
        this.props.handleSteps(20);
      }

    } else {
      this.props.handleSteps(7);
    }
  };

  handleProfileTypeChange = () => {
    this.props.handleSteps(this.props.editProject ? 1 : 2);
  };

  renderProfileTypeSwitch = (isIndividual) => (
    <ProfileTypeSwitch>
      <ProfileTypeSwitchText>
        {isIndividual ? 'Keine Privatperson?' : 'Keine Organisation?'}
      </ProfileTypeSwitchText>
      <ProfileTypeSwitchButton
        type="button"
        onClick={this.handleProfileTypeChange}
      >
        Profiltyp ändern
      </ProfileTypeSwitchButton>
    </ProfileTypeSwitch>
  );

  handleAvatarSelect = (avatarKey) => {
    this.setState({ selectedAvatar: avatarKey });
    this.formRef.current?.setFieldsValue({ selected_avatar: avatarKey });
  };

  handleVisibilityChange = (visibility) => {
    const normalizedVisibility = this.normalizeVisibility(visibility);

    this.setState({
      visibility: normalizedVisibility,
      documentError:
        normalizedVisibility === 'anonymous' ? this.state.documentError : '',
    });
    this.formRef.current?.setFieldsValue({ visibility: normalizedVisibility });
    this.props.setCanMove(false);
  };

  openVisibilityInfoModal = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.setState({ showVisibilityInfoModal: true });
  };

  closeVisibilityInfoModal = () => {
    this.setState({ showVisibilityInfoModal: false });
  };

  onFinish = (values) => {
    const {
      updateDraft,
      uploadFiles,
      projectId,
      setCanMove,
      showModal,
      selectedStep,
      setSubmitFromModal,
      handleSteps,
    } = this.props;

    const visibility = this.normalizeVisibility(
      values.visibility || this.state.visibility
    );
    const requiresIdentityDocument =
      !this.props.editProject &&
      this.props.project?.business_type === 'individual' &&
      visibility === 'anonymous';
    const hasIdentityDocument =
      this.state.files.length > 0 || this.state.existingDocs.length > 0;

    if (requiresIdentityDocument && !hasIdentityDocument) {
      const message =
        'Bitte lade ein gültiges Ausweisdokument hoch. Auch bei einem anonymen Projekt müssen wir deine Identität intern verifizieren; öffentlich bleibst du anonym.';

      this.setState({ documentError: message }, () => {
        this.documentsSectionRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      });
      AlertActions.error(message, 'Ausweisdokument fehlt');
      return;
    }

    const payload = {
      ...values,
      selected_avatar: this.getSelectedAvatarBaseKey(),
      visibility,
      organization_name:
        values.organization_name || this.state.organization_name || null,
      organization_type:
        values.organization_type || this.state.organization_type || null,
      belege_email:
        values.belege_email ||
        this.state.belege_email ||
        this.props.project?.initiator_email ||
        null,
      donation_receipts_possible: values.donation_receipts_possible || false,
      isPersonalizationDone: true,
      is_personalization_done: true,
    };


    const formData = new FormData();

    Object.entries(payload).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(`project[${key}]`, value);
      }
    });

    if (this.state.organizationLogoFile) {
      formData.append(
        'project[organization_logo]',
        this.state.organizationLogoFile
      );
    }

    if (this.state.removeOrganizationLogoRequested) {
      formData.append('project[remove_organization_logo]', 'true');
    }

    if (this.state.files.length > 0 && uploadFiles && projectId) {
      const filesFormData = new FormData();

      this.state.files.forEach((file) => {
        filesFormData.append('files[]', file);
      });

      uploadFiles(projectId, filesFormData);
    }

    if (showModal) {
      updateDraft(projectId, formData, selectedStep - 1);
      setSubmitFromModal(false);
    } else {
      updateDraft(projectId, formData);
      handleSteps(8);
    }

    setCanMove(true);
  };

  render() {
    const { loading } = this.props;
    const {
      files,
      selectedAvatar,
      existingDocs,
      documentError,
    } = this.state;
    const isIndividual = this.props.project?.business_type === 'individual';
    const isEvent = this.isEventProject();
    const personalizationLocked = this.isPersonalizationLocked();
    return (
      <Container style={{ maxWidth: 600 }}>
        <Spin spinning={loading}>
          <Content>
            {this.props.project && !isIndividual && (
              <ProfileHeaderSection>
                <SectionTitle>Angaben zu deiner Organisation</SectionTitle>
                {!this.props.editProject && this.renderProfileTypeSwitch(false)}
              </ProfileHeaderSection>
            )}

            {this.props.project && isIndividual && (
              <ProfileHeaderSection>
                <SectionTitle>Persönliche Angaben</SectionTitle>
                {!this.props.editProject && this.renderProfileTypeSwitch(true)}
                {!isEvent && (
                  <SectionText>
                    Bei jedem Projekt geht es um eine Amanah – ein anvertrautes
                    Gut. Damit Unterstützer wissen, wohin ihre Sadaqah fließt
                    und wem sie anvertraut wird, ist dein Name wichtig.
                    Anonymität ist nur in besonderen Ausnahmefällen möglich (z.
                    B. bei Krankheit oder Schulden).
                  </SectionText>
                )}
              </ProfileHeaderSection>
            )}

            <StyledForm
              {...formLayout}
              ref={this.formRef}
              name="personal-information"
              onFinish={this.onFinish}
              onChange={() => this.props.setCanMove(false)}
              validateMessages={formValidateMessages}
              initialValues={{
                visibility: this.normalizeVisibility(this.state.visibility),
                selected_avatar: this.state.selectedAvatar,
                organization_name: this.state.organization_name,
                organization_type: this.state.organization_type,
                donation_receipts_possible:
                  this.state.donation_receipts_possible,
                belege_email: this.state.belege_email,
              }}
            >
              <FormContent>
                {this.props.project && isIndividual && (
                  <>
                    <Section>
                      <FormItem
                        name="visibility"
                        label={
                          <LabelRow>
                            <LabelText>Deine Sichtbarkeit</LabelText>
                            <RequiredMark>*</RequiredMark>
                            <HelpIconWrap
                              type="button"
                              aria-label="Was bedeutet Sichtbarkeit?"
                              onClick={this.openVisibilityInfoModal}
                            >
                              <QuestionMark />
                            </HelpIconWrap>
                          </LabelRow>
                        }
                      >
                        <SelectWrap>
                          <Select
                            options={this.getVisibilityOptions()}
                            value={this.normalizeVisibility(
                              this.state.visibility
                            )}
                            disabled={personalizationLocked}
                            onChange={this.handleVisibilityChange}
                          />
                        </SelectWrap>
                      </FormItem>

                      <HintText>
                        Entscheide, wie viel du von dir preisgeben möchtest.
                      </HintText>
                    </Section>

                    <Section>
                      <LabelText>Avatar auswählen</LabelText>

                      <AvatarRow>
                        {avatarOptions.map((avatar) => (
                          <AvatarButton
                            key={avatar.key}
                            type="button"
                            $bg={avatar.bg}
                            disabled={personalizationLocked}
                            $active={selectedAvatar === avatar.key}
                            onClick={() => this.handleAvatarSelect(avatar.key)}
                          >
                            <AvatarInner $color={avatar.color}>
                              {avatar.icon}
                            </AvatarInner>
                          </AvatarButton>
                        ))}
                      </AvatarRow>
                    </Section>
                  </>
                )}

                {this.props.project && !isIndividual && (
                  <>
                    <FormItem
                      name="organization_name"
                      label={
                        <AddFormItemLabel>
                          Name deiner Organisation
                        </AddFormItemLabel>
                      }
                      required={false}
                      rules={[
                        {
                          required: true,
                          message: 'Name deiner Organisation ist erforderlich',
                        },
                      ]}
                    >
                      <AddTextArea
                        disabled={personalizationLocked}
                        placeholder="Name deiner Organisation"
                        rows={1}
                        showCount
                        maxLength={100}
                        onChange={(e) =>
                          this.setState({ organization_name: e.target.value })
                        }
                      />
                    </FormItem>

                    <Section>
                      <FormItem
                        name="organization_type"
                        label={
                          <LabelRow>
                            <LabelText>Organisationstyp</LabelText>
                            <RequiredMark>*</RequiredMark>
                          </LabelRow>
                        }
                      >
                        <SelectWrap>
                          <Select
                            disabled={personalizationLocked}
                            placeholder="Organisationstyp auswählen"
                            value={this.state.organization_type}
                            options={organizationTypeOptions}
                            onChange={(value) =>
                              this.setState({ organization_type: value })
                            }
                          />
                        </SelectWrap>
                      </FormItem>
                    </Section>

                    <LogoSection>
                      <LabelText>Vorschau</LabelText>

                      <LogoRow>
                        <LogoAvatarWrap>
                          {this.state.organizationLogoPreview ? (
                            <LogoImage
                              src={this.state.organizationLogoPreview}
                              alt="Organisation Logo"
                            />
                          ) : (
                            <SupporterIcon />
                          )}

                          <LogoBadge>
                            <BlueCheck />
                          </LogoBadge>
                        </LogoAvatarWrap>

                        <LogoTexts>
                          <LogoTitle>
                            {this.state.organization_name ||
                              'Deine Organisation'}
                          </LogoTitle>
                          <LogoSubtitle>
                            {this.getOrganizationTypeLabel()}
                          </LogoSubtitle>
                        </LogoTexts>
                      </LogoRow>

                      {!personalizationLocked && (
                        <LogoActions>
                          <LogoActionButton
                            type="button"
                            $tone="green"
                            onClick={this.handleOrganizationLogoClick}
                          >
                            <UploadOutlined />
                            <LogoActionText>Logo hochladen</LogoActionText>
                          </LogoActionButton>

                          <LogoActionButton
                            type="button"
                            $tone="grey"
                            onClick={this.removeOrganizationLogo}
                          >
                            <DeleteOutlined />
                            <LogoActionText>Logo entfernen</LogoActionText>
                          </LogoActionButton>
                        </LogoActions>
                      )}

                      <HiddenFileInput
                        ref={this.organizationLogoInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        onChange={this.handleOrganizationLogoPick}
                      />
                    </LogoSection>
                  </>
                )}

                {this.props.project && isIndividual && (
                  <Section>
                    <LabelText>Vorschau</LabelText>

                    <PreviewCard>
                      <PreviewHeader>
                        {(() => {
                          const avatar = this.getSelectedAvatarOption();

                          return (
                            <PreviewAvatar $bg={avatar?.bg}>
                              {avatar ? (
                                <AvatarInner $color={avatar.color}>
                                  {avatar.icon}
                                </AvatarInner>
                              ) : (
                                <SupporterIcon />
                              )}

                              <PreviewBadge>
                                <ReceiptCheck />
                              </PreviewBadge>
                            </PreviewAvatar>
                          );
                        })()}

                        <PreviewTexts>
                          <PreviewTitle>
                            {this.state.visibility === 'anonymous'
                              ? 'Anonymer Projektinitiator'
                              : this.state.visibility === 'verified'
                              ? shortenName(
                                  this.props.user?.first_name,
                                  this.props.user?.last_name
                                )
                              : getFullName(
                                  this.props.user?.first_name,
                                  this.props.user?.last_name
                                )}
                          </PreviewTitle>

                          <PreviewMeta>
                            aus {this.props.project.categoryType.city}
                          </PreviewMeta>
                        </PreviewTexts>
                      </PreviewHeader>
                    </PreviewCard>
                  </Section>
                )}

                {this.props.project &&
                  isIndividual &&
                  this.state.visibility === 'anonymous' && (
                    <InfoBox>
                      <InfoIconWrap>
                        <ReceiptCheck />
                      </InfoIconWrap>

                      <InfoContent>
                        <InfoTitle>
                          Anonymität mit bestätigter Identität
                        </InfoTitle>

                        <InfoText>
                          In besonderen Fällen – etwa bei Schulden, Krankheit,
                          familiären Konflikten oder Sicherheitsrisiken – kannst
                          du dein Projekt anonym starten.
                        </InfoText>

                        <InfoText>
                          Damit das möglich ist, musst du zuvor deine Identität
                          bestätigen. Lade dafür ein gültiges Ausweisdokument
                          hoch. Deine Daten werden intern geprüft, aber nicht
                          öffentlich sichtbar und nach den Vorgaben der DSGVO
                          sicher gespeichert und gelöscht.
                        </InfoText>

                        <InfoText>
                          Vertrauen ist die Grundlage jeder Sadaqah – sie ist
                          eine Amanah zwischen dir, der Community und den
                          Unterstützern. Wir behandeln alle Daten vertraulich
                          und geben sie niemals weiter. Mehr dazu findest du in
                          unseren{' '}
                          <PrivacyLink
                            href="/datenschutz"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Datenschutzrichtlinien
                          </PrivacyLink>
                          .
                        </InfoText>
                      </InfoContent>
                    </InfoBox>
                  )}

                {this.props.project &&
                  isIndividual &&
                  this.state.visibility !== 'anonymous' && (
                    <InfoBox>
                      <InfoIconWrap>
                        <ReceiptCheck />
                      </InfoIconWrap>

                      <InfoContent>
                        <InfoTitle>
                          Bestätige deine Identität und erhalte das
                          commonsplace-Siegel
                        </InfoTitle>

                        <InfoText>
                          Damit dein Projekt mehr Vertrauen und Sichtbarkeit in
                          der Community gewinnt, kannst du dich optional
                          verifizieren lassen. Lade dafür einfach deinen
                          Personalausweis hoch und erhalte das
                          commonsplace-Siegel – ein Zeichen für Transparenz,
                          Sicherheit und Verlässlichkeit.
                        </InfoText>

                        <InfoText>
                          Wenn du dich entscheidest, vollständig anonym zu
                          bleiben, musst du aus Sicherheitsgründen ein
                          Ausweisdokument hochladen. Deine Daten werden
                          selbstverständlich streng vertraulich behandelt und
                          niemals an Dritte weitergegeben. Mehr dazu findest du
                          in unseren{' '}
                          <PrivacyLink
                            href="/datenschutz"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Datenschutzrichtlinien
                          </PrivacyLink>
                          .
                        </InfoText>
                      </InfoContent>
                    </InfoBox>
                  )}

                {this.props.project && !isIndividual && (
                  <InfoBox>
                    <InfoIconWrap>
                      <CheckMarkIcon />
                    </InfoIconWrap>

                    <InfoContent>
                      <InfoTitle>
                        Bestätige deine Organisation und erhalte unser Siegel
                        (optional)
                      </InfoTitle>

                      <InfoText>
                        Damit deine Organisation für die Community Vertrauen und
                        Sichtbarkeit vermittelt, kannst du dich verifizieren
                        lassen. Lade dafür deine Unterlagen hoch und erhalte das
                        commonsplace-Siegel – als Zeichen für Transparenz,
                        Sicherheit und Verlässlichkeit.
                      </InfoText>

                      <InfoTitle>Welche Unterlagen brauchst du?</InfoTitle>

                      <InfoText>
                        <ul style={{ marginBottom: 0 }}>
                          <li>
                            Freistellungsbescheid (Nachweis der Gemeinnützigkeit
                            vom Finanzamt)
                          </li>
                          <li>Satzung oder Vereins-/Handelsregisterauszug</li>
                        </ul>
                      </InfoText>

                      <InfoText>
                        Zusätzlich kannst du noch weitere Angaben ergänzen, um
                        eine verifizierte Partnerorganisation von commonsplace
                        zu werden. Mehr Informationen dazu findest du nach
                        Einreichung deines Projekts.
                      </InfoText>
                    </InfoContent>
                  </InfoBox>
                )}

                <Section ref={this.documentsSectionRef}>
                  <input
                    ref={this.fileInputRef}
                    type="file"
                    multiple
                    accept=".pdf,.png,.jpg,.jpeg"
                    style={{ display: 'none' }}
                    onChange={this.handleFilePick}
                  />

                  {!personalizationLocked && (
                    <UploadCard>
                      <UploadArea
                        type="button"
                        $hasError={Boolean(documentError)}
                        aria-invalid={Boolean(documentError)}
                        aria-describedby={
                          documentError ? 'identity-document-error' : undefined
                        }
                        onClick={this.handleUploadAreaClick}
                        onDrop={this.handleDrop}
                        onDragOver={(event) => event.preventDefault()}
                      >
                        <UploadIcon>
                          <UploadOutlined />
                        </UploadIcon>

                        <UploadTopText>
                          <strong>Klicken zum Hochladen</strong> oder Datei
                          einfach reinziehen
                        </UploadTopText>

                        <UploadHint>PDF, PNG oder JPEG (max. 3 MB)</UploadHint>
                      </UploadArea>

                      {documentError && (
                        <UploadError
                          id="identity-document-error"
                          role="alert"
                        >
                          {documentError}
                        </UploadError>
                      )}
                    </UploadCard>
                  )}

                  {(files.length > 0 || existingDocs.length > 0) && (
                    <Queue>
                      {existingDocs.map((doc) => (
                        <QueueItem key={`existing-${doc.id}`}>
                          <QueueContent>
                            <FileOutlined />

                            <QueueTexts>
                              <QueueTitle>{doc.filename}</QueueTitle>
                              <QueueMeta>
                                {doc.byte_size < 1024 * 1024
                                  ? `${(doc.byte_size / 1024).toFixed(0)} KB`
                                  : `${(doc.byte_size / 1024 / 1024).toFixed(
                                      1
                                    )} MB`}{' '}
                                – Hochgeladen
                              </QueueMeta>
                            </QueueTexts>
                          </QueueContent>

                          {!personalizationLocked && (
                            <RemoveButton
                              type="button"
                              onClick={() => this.removeExistingDoc(doc.id)}
                            >
                              <DeleteOutlined />
                            </RemoveButton>
                          )}
                        </QueueItem>
                      ))}

                      {files.map((file, index) => (
                        <QueueItem key={`${file.name}-${index}`}>
                          <QueueContent>
                            <FileOutlined />

                            <QueueTexts>
                              <QueueTitle>{file.name}</QueueTitle>
                              <QueueMeta>
                                {file.size < 1024 * 1024
                                  ? `${(file.size / 1024).toFixed(0)} KB`
                                  : `${(file.size / 1024 / 1024).toFixed(
                                      1
                                    )} MB`}{' '}
                                – Bereit zum Hochladen
                              </QueueMeta>
                            </QueueTexts>
                          </QueueContent>

                          <RemoveButton
                            type="button"
                            onClick={() => this.removeFile(index)}
                          >
                            <DeleteOutlined />
                          </RemoveButton>
                        </QueueItem>
                      ))}
                    </Queue>
                  )}
                </Section>

                {!isIndividual && (
                  <Section>
                    <SectionTitle>Belege</SectionTitle>

                    <FormItem
                      name="donation_receipts_possible"
                      valuePropName="checked"
                    >
                      <Checkbox
                        disabled={personalizationLocked}
                        onChange={(e) =>
                          this.setState({
                            donation_receipts_possible: e.target.checked,
                          })
                        }
                        style={{
                          fontSize: 16,
                          fontWeight: 500,
                          fontFamily: 'Inter, sans-serif',
                          color: '#414651',
                        }}
                      >
                        Ich kann Spendenquittungen ausstellen
                      </Checkbox>
                    </FormItem>

                    <ReceiptDescription>
                      Wenn deine Organisation gemeinnützig ist, können
                      Unterstützer Spendenquittungen erhalten. Das erhöht
                      nachweislich die Spendenbereitschaft. Die Ausstellung der
                      Spendenquittungen liegt in deiner Verantwortung. Du wirst
                      per E-Mail benachrichtigt, wenn ein Nutzer eine
                      Spendenquittung anfordert.
                    </ReceiptDescription>
                  </Section>
                )}

                {this.state.donation_receipts_possible && (
                  <FormItem
                    name="belege_email"
                    label={
                      <AddFormItemLabel>
                        E-Mail für Spendenquittungsanfragen
                      </AddFormItemLabel>
                    }
                    required={false}
                    rules={[
                      {
                        required: true,
                        message:
                          'E-Mail ist erforderlich, damit du Spendenquittungen ausstellen kannst.',
                      },
                      {
                        type: 'email',
                        message: 'Bitte gib eine gültige E-Mail-Adresse ein.',
                      },
                    ]}
                  >
                    <AddTextArea
                      placeholder="E-Mail für Spendenquittungsanfragen"
                      showCount
                      rows={1}
                      maxLength={100}
                    />
                  </FormItem>
                )}

                <FormItem>
                  <Actions>
                    <SecondaryButton
                      type="button"
                      minWidth="117px"
                      onClick={this.handleReturn}
                    >
                      {this.props.editProject ? (
                        <p>Zurück zur Projektübersicht</p>
                      ) : (
                        <>
                          <ArrowLeftOutlined />
                          <p>Zurück</p>
                        </>
                      )}
                    </SecondaryButton>

                    <PrimaryButtonV2
                      type="primary"
                      htmltype="submit"
                      minWidth="113px"
                    >
                      {this.props.editProject ? (
                        <p>Speichern</p>
                      ) : (
                        <>
                          <p>Weiter</p>
                          <ArrowRightOutlined />
                        </>
                      )}
                    </PrimaryButtonV2>
                  </Actions>
                </FormItem>
              </FormContent>
            </StyledForm>
          </Content>

          <Modal
            open={this.state.showVisibilityInfoModal}
            footer={null}
            closable={false}
            centered
            width={600}
            destroyOnClose
            onCancel={this.closeVisibilityInfoModal}
            bodyStyle={{ padding: 0 }}
          >
            <VisibilityModalContent>
              <VisibilityModalClose
                type="button"
                aria-label="Modal schließen"
                onClick={this.closeVisibilityInfoModal}
              >
                <CloseOutlined />
              </VisibilityModalClose>

              <VisibilityModalBody>
                <VisibilityModalIcon>
                  <UserOutlined />
                </VisibilityModalIcon>

                <VisibilityModalSection>
                  <VisibilityModalTitle>
                    Was bedeutet Sichtbarkeit?
                  </VisibilityModalTitle>
                  <VisibilityModalText>
                    Unterstützungen sind Sadaqah - und damit ein anvertrautes
                    Gut. Wahrhaftigkeit und Vertrauen sind entscheidend, damit
                    deine Projekte von der Community getragen werden können.
                  </VisibilityModalText>
                </VisibilityModalSection>

                <VisibilityModalSection>
                  <VisibilityModalSubtitle>
                    Personen mit Namen (empfohlen)
                  </VisibilityModalSubtitle>
                  <VisibilityModalText>
                    Du kannst dein Projekt auch ohne Verifizierung starten. Wenn
                    du aber deinen Personalausweis hochlädst, erhältst du unser
                    commonsplace-Siegel. Damit zeigst du, dass deine Identität
                    bestätigt wurde - das schafft mehr Vertrauen und
                    Sichtbarkeit.
                  </VisibilityModalText>
                </VisibilityModalSection>

                <VisibilityModalSection>
                  <VisibilityModalSubtitle>
                    Anonyme Personen
                  </VisibilityModalSubtitle>
                  <VisibilityModalText>
                    Wir erlauben auch Projekte, die anonym gestartet werden -
                    zum Beispiel bei sensiblen Themen wie Schulden, Krankheit
                    oder ähnlichen persönlichen Geschichten. Damit die Community
                    dennoch geschützt ist, ist es Pflicht ein Ausweisdokument
                    hochzuladen. Dieses wird ausschließlich zur Sicherheit
                    geprüft.
                  </VisibilityModalText>
                </VisibilityModalSection>
              </VisibilityModalBody>

              <VisibilityModalActions>
                <VisibilityModalButton
                  type="button"
                  onClick={this.closeVisibilityInfoModal}
                >
                  Verstanden
                </VisibilityModalButton>
              </VisibilityModalActions>
            </VisibilityModalContent>
          </Modal>
        </Spin>
      </Container>
    );
  }
}

export function shortenName(firstname, lastname) {
  return `${firstname?.charAt(0).toUpperCase()}${firstname?.slice(1)} ${lastname
    ?.charAt(0)
    .toUpperCase()}.`;
}

export function getFullName(firstname, lastname) {
  return `${firstname?.charAt(0).toUpperCase()}${firstname?.slice(1)} ${lastname
    ?.charAt(0)
    .toUpperCase()}${lastname?.slice(1)}`;
}

const mapStateToProps = (state) => {
  const { project, loading, errors, currentStep } = state.projects;
  const { user } = state.authentication;

  return {
    project,
    loading,
    errors,
    currentStep,
    user,
  };
};

const mapDispatchToProps = (dispatch) => ({
  handleSteps: (step) => dispatch(ProjectsActions.handleSteps(step)),

  updateDraft: (projectId, project, currentStep, submit) =>
    dispatch(
      ProjectsActions.updateDraft(projectId, project, currentStep, submit)
    ),

  uploadFiles: (projectId, formData) =>
    dispatch(ProjectsActions.uploadFiles(projectId, formData)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(PersonalInformation);
